import Decimal from 'decimal.js'
import { assetById, methodology } from './data/catalog'
import type {
  CalculationInput,
  CalculationMethod,
  CalculationResult,
  BreakdownItem,
  NumericPoint,
  SourcePoint,
} from './types'

type TableSeries = {
  table: string
  unit: string
  points: NumericPoint[]
  highExtrapolation?: 'regression3'
  capAt?: number
}

type PipeTable = {
  table: string
  unit: string
  series: Record<string, NumericPoint[]>
}

type NoncircularTable = {
  table: string
  unit: string
  series: Record<string, NumericPoint[]>
}

type SimplifiedTable = {
  table: string
  rows: Array<{ sector: string; label: string; coefficient: number }>
}

const tables = methodology.tables as Record<string, TableSeries | PipeTable | NoncircularTable | SimplifiedTable>
const vatDivisor = new Decimal(1).plus(methodology.meta.vatRate)

Decimal.set({ precision: 40, rounding: Decimal.ROUND_HALF_UP })

const METHOD_LABELS: Record<CalculationMethod, string> = {
  exact: 'Přesná hodnota metodiky',
  interpolated: 'Lineární interpolace',
  extrapolated: 'Pracovní extrapolace',
  fixed: 'Pevná sazba metodiky',
  capped: 'Pevná hodnota pro horní pásmo',
  converted: 'Zjednodušený přepočet',
}

interface Evaluation {
  value: Decimal
  method: CalculationMethod
  sourcePoints: SourcePoint[]
  formula: string
}

function decimal(value: Decimal.Value): Decimal {
  return new Decimal(value)
}

function crown(value: Decimal.Value): number {
  return decimal(value).toDecimalPlaces(0, Decimal.ROUND_HALF_UP).toNumber()
}

function exactNumber(value: Decimal.Value): number {
  return decimal(value).toNumber()
}

function formulaNumber(value: Decimal.Value, maximumDecimals = 12): string {
  return decimal(value).toFixed(maximumDecimals).replace(/(?:\.0+|(?:(\.\d*?[1-9]))0+)$/, '$1')
}

function positive(value: number, label: string): number {
  if (!Number.isFinite(value) || value <= 0) {
    throw new Error(`${label} musí být větší než nula.`)
  }
  return value
}

function nonNegative(value: number, label: string): number {
  if (!Number.isFinite(value) || value < 0) {
    throw new Error(`${label} nesmí být záporná hodnota.`)
  }
  return value
}

function linearValue(a: SourcePoint, b: SourcePoint, x: number): Decimal {
  return decimal(a.value).plus(
    decimal(x).minus(a.x).times(decimal(b.value).minus(a.value)).div(decimal(b.x).minus(a.x)),
  )
}

function regression(points: SourcePoint[], x: number): Decimal {
  const count = decimal(points.length)
  const meanX = points.reduce((sum, point) => sum.plus(point.x), decimal(0)).div(count)
  const meanY = points.reduce((sum, point) => sum.plus(point.value), decimal(0)).div(count)
  const numerator = points.reduce(
    (sum, point) => sum.plus(decimal(point.x).minus(meanX).times(decimal(point.value).minus(meanY))),
    decimal(0),
  )
  const denominator = points.reduce(
    (sum, point) => sum.plus(decimal(point.x).minus(meanX).pow(2)),
    decimal(0),
  )
  const slope = numerator.div(denominator)
  return meanY.plus(slope.times(decimal(x).minus(meanX)))
}

function evaluate(
  rawPoints: NumericPoint[],
  x: number,
  field: 'value' | 'total' | 'building' | 'technology' = 'value',
  options: { highRegression?: boolean; capAt?: number } = {},
): Evaluation {
  const points = rawPoints
    .filter((point) => typeof point[field] === 'number')
    .map((point) => ({ x: point.x, value: point[field] as number }))
    .sort((a, b) => a.x - b.x)

  if (points.length < 2) {
    throw new Error('Pro výpočet nejsou dostupné alespoň dva zdrojové body.')
  }

  if (options.capAt && x >= options.capAt) {
    const cap = points.find((point) => point.x === options.capAt) ?? points.at(-1)!
    return {
      value: decimal(cap.value),
      method: x === cap.x ? 'exact' : 'capped',
      sourcePoints: [cap],
      formula: x === cap.x ? `Přesná tabulková hodnota pro ${x}.` : `Pro ${options.capAt} a více se používá poslední hodnota ${cap.value}.`,
    }
  }

  const exact = points.find((point) => point.x === x)
  if (exact) {
    return { value: decimal(exact.value), method: 'exact', sourcePoints: [exact], formula: `Přesná tabulková hodnota pro ${x}.` }
  }

  const betweenIndex = points.findIndex((point) => point.x > x)
  if (betweenIndex > 0) {
    const a = points[betweenIndex - 1]
    const b = points[betweenIndex]
    const value = linearValue(a, b, x)
    if (value.lte(0)) throw new Error('Interpolace vedla k nulové nebo záporné hodnotě.')
    return {
      value,
      method: 'interpolated',
      sourcePoints: [a, b],
      formula: `${a.value} + (${x} − ${a.x}) × (${b.value} − ${a.value}) / (${b.x} − ${a.x}) = ${formulaNumber(value)}`,
    }
  }

  const high = x > points.at(-1)!.x
  const sourcePoints = high
    ? options.highRegression ? points.slice(-3) : points.slice(-2)
    : points.slice(0, 2)
  const value = high && options.highRegression
    ? regression(sourcePoints, x)
    : linearValue(sourcePoints[0], sourcePoints[1], x)
  if (value.lte(0)) throw new Error('Extrapolace vedla k nulové nebo záporné hodnotě. Výsledek je nutné určit individuálně.')
  const formula = high && options.highRegression
    ? `Lineární regrese posledních tří bodů pro parametr ${x}; výsledek ${formulaNumber(value)}.`
    : `${sourcePoints[0].value} + (${x} − ${sourcePoints[0].x}) × (${sourcePoints[1].value} − ${sourcePoints[0].value}) / (${sourcePoints[1].x} − ${sourcePoints[0].x}) = ${formulaNumber(value)}`
  return { value, method: 'extrapolated', sourcePoints, formula }
}

function combineMethod(methods: CalculationMethod[]): CalculationMethod {
  if (methods.includes('extrapolated')) return 'extrapolated'
  if (methods.includes('interpolated')) return 'interpolated'
  if (methods.includes('capped')) return 'capped'
  if (methods.every((method) => method === 'fixed')) return 'fixed'
  return 'exact'
}

function indicatorNet(gross: Decimal.Value): number {
  return exactNumber(decimal(gross).div(vatDivisor))
}

function moneyNet(gross: Decimal.Value): number {
  return crown(decimal(gross).div(vatDivisor))
}

function makeBreakdown(
  parts: Array<{ label: string; rawGross: Decimal.Value }>,
  _totalGross: number,
  _totalNet: number,
): BreakdownItem[] {
  return parts.map((part) => ({
    label: part.label,
    gross: crown(part.rawGross),
    net: moneyNet(part.rawGross),
  }))
}

function findK5(id: number) {
  const entry = methodology.k5.find((item) => item.id === id)
  if (!entry) throw new Error('Vyberte platnou velikost obce.')
  return entry
}

function makeBaseResult(
  input: CalculationInput,
  values: Omit<CalculationResult, 'assetId' | 'assetLabel' | 'sector' | 'methodLabel' | 'calculatedAt'>,
): CalculationResult {
  const asset = assetById.get(input.assetId)
  if (!asset) throw new Error('Vybraný druh majetku nebyl nalezen.')
  return {
    ...values,
    assetId: asset.id,
    assetLabel: asset.label,
    sector: asset.sector,
    methodLabel: METHOD_LABELS[values.method],
    calculatedAt: new Date().toISOString(),
  }
}

function getPipeTable(assetId: string): PipeTable {
  const key = assetById.get(assetId)?.tableKey
  const table = key ? tables[key] as PipeTable : undefined
  if (!table?.series) throw new Error('Zdrojová tabulka potrubí nebyla nalezena.')
  return table
}

export function getPipeMaterials(assetId: string): string[] {
  const table = getPipeTable(assetId)
  return [...new Set(Object.keys(table.series).map((key) => key.split('|')[1]))]
}

function getPipeEvaluation(assetId: string, material: string, surface: 'z' | 'n', dn: number): Evaluation {
  const table = getPipeTable(assetId)
  const entry = Object.entries(table.series).find(([key]) => {
    const parts = key.split('|')
    return parts[1] === material && parts[2] === surface
  })
  if (!entry) throw new Error('Pro zvolený materiál a způsob uložení nejsou zdrojové hodnoty.')
  return evaluate(entry[1], dn)
}

function calculatePipe(input: CalculationInput): CalculationResult {
  const asset = assetById.get(input.assetId)!
  const dn = positive(input.dn, 'DN')
  const length = positive(input.length, 'Délka potrubí')
  const k5 = findK5(input.k5Id)
  const material = input.material === '__other__' ? input.baseMaterial : input.material
  const materialCoefficient = input.material === '__other__'
    ? input.materialCoefficient
    : 1
  if (materialCoefficient < 0.8 || materialCoefficient > 1.8) {
    throw new Error('Koeficient jiného materiálu musí být v intervalu 0,8 až 1,8.')
  }

  const evaluated = getPipeEvaluation(asset.id, material, input.surface, dn)
  const collectorCoefficient = asset.id === 'water-pipe' && input.collector ? 0.7 : 1
  const indicator = evaluated.value.times(materialCoefficient).times(collectorCoefficient)
  const indicatorGross = exactNumber(indicator)
  const crossingLength = nonNegative(input.crossingLength, 'Délka podchodu nebo shybky')
  const crossingRate = nonNegative(input.crossingRate, 'Cena podchodu nebo shybky')
  if (crossingRate > 607_600) throw new Error('Cena podchodu nebo shybky může být nejvýše 607 600 Kč/bm.')
  const crossingCost = decimal(crossingLength).times(crossingRate)
  const atypicalCost = nonNegative(input.atypicalCost, 'Atypické náklady')
  const pipeCost = indicator.times(length).times(k5.value)
  const rawTotal = pipeCost.plus(crossingCost).plus(atypicalCost)
  const totalGross = crown(rawTotal)
  const totalNet = moneyNet(rawTotal)
  const adjustments = [`Polohový koeficient K5: ${k5.value.toFixed(2).replace('.', ',')}`]
  if (materialCoefficient !== 1) adjustments.push(`Koeficient jiného materiálu: ${materialCoefficient.toFixed(2).replace('.', ',')}`)
  if (collectorCoefficient !== 1) adjustments.push('Potrubí v kolektoru: 70 % tabulkové hodnoty')
  if (!crossingCost.isZero()) adjustments.push(`Podchod nebo shybka: ${formatMoney(crossingCost)}`)
  if (atypicalCost) adjustments.push(`Atypické náklady: ${formatMoney(atypicalCost)}`)
  const warnings = evaluated.method === 'extrapolated'
    ? ['Hodnota DN je mimo rozsah zdrojové řady a je pracovně extrapolována.']
    : []
  if (input.material === '__other__') warnings.push('Jiný materiál vyžaduje odborné potvrzení zvolené nejbližší skupiny a koeficientu.')

  return makeBaseResult(input, {
    inputSummary: `DN ${dn}, ${input.material === '__other__' ? `jiný materiál (${material})` : material}, ${input.surface === 'z' ? 'zpevněná plocha' : 'nezpevněná plocha'}, ${length.toLocaleString('cs-CZ')} bm`,
    indicatorLabel: 'Měrný cenový ukazatel Cmu',
    indicatorGross,
    indicatorNet: indicatorNet(indicator),
    indicatorUnit: 'Kč/bm',
    totalGross,
    totalNet,
    method: evaluated.method,
    formula: `${evaluated.formula} Celkem: ${formulaNumber(indicator)} × ${length} × ${k5.value}${!crossingCost.isZero() ? ` + ${formulaNumber(crossingCost)}` : ''}${atypicalCost ? ` + ${formulaNumber(atypicalCost)}` : ''}; konečná cena ${totalGross} Kč.`,
    sourceLabel: `Metodický pokyn ${methodology.meta.methodology}, tabulka č. ${asset.table}`,
    sourcePoints: evaluated.sourcePoints,
    breakdown: makeBreakdown([
      { label: 'Potrubí', rawGross: pipeCost },
      ...(!crossingCost.isZero() ? [{ label: 'Podchod nebo shybka', rawGross: crossingCost }] : []),
      ...(atypicalCost ? [{ label: 'Atypické náklady', rawGross: decimal(atypicalCost) }] : []),
    ], totalGross, totalNet),
    warnings,
    adjustments,
  })
}

function calculateNoncircular(input: CalculationInput): CalculationResult {
  const table = tables.noncircular as NoncircularTable
  const points = table.series[input.variant]
  if (!points) throw new Error('Vyberte platný druh profilu.')
  const primary = positive(input.dn, 'První rozměr profilu')
  const secondary = positive(input.secondaryParameter, 'Druhý rozměr profilu')
  const length = positive(input.length, 'Délka stoky')
  const k5 = findK5(input.k5Id)
  const evaluated = evaluate(points, primary)
  const exactProfile = points.find((point) => point.x === primary && point.secondary === secondary)
  const method = exactProfile ? evaluated.method : evaluated.method === 'exact' ? 'interpolated' : evaluated.method
  const rawTotal = evaluated.value.times(length).times(k5.value).plus(nonNegative(input.atypicalCost, 'Atypické náklady'))
  const totalGross = crown(rawTotal)
  const warnings = method === 'exact' ? [] : [
    'U nestandardního profilu je cena pracovně odvozena pouze podle prvního rozměru. Druhý rozměr výpočet ceny nemění.',
  ]
  if (method === 'extrapolated') warnings.push('První rozměr je mimo rozsah tabulky č. 10.')
  return makeBaseResult(input, {
    inputSummary: `${input.variant === 'tlamove' ? 'Tlamový' : 'Vejčitý'} profil ${primary}/${secondary}, ${length.toLocaleString('cs-CZ')} bm`,
    indicatorLabel: 'Měrný cenový ukazatel Cmu',
    indicatorGross: exactNumber(evaluated.value),
    indicatorNet: indicatorNet(evaluated.value),
    indicatorUnit: 'Kč/bm',
    totalGross,
    totalNet: moneyNet(rawTotal),
    method,
    formula: `${evaluated.formula} Celkem: ${formulaNumber(evaluated.value)} × ${length} × ${k5.value}; konečná cena ${totalGross} Kč.`,
    sourceLabel: `Metodický pokyn ${methodology.meta.methodology}, tabulka č. 10`,
    sourcePoints: evaluated.sourcePoints,
    breakdown: [],
    warnings,
    adjustments: [`Polohový koeficient K5: ${k5.value.toFixed(2).replace('.', ',')}`],
  })
}

function calculateTableCu(input: CalculationInput): CalculationResult {
  const asset = assetById.get(input.assetId)!
  const parameter = positive(input.parameter, asset.parameterLabel ?? 'Technický parametr')
  const k5 = findK5(input.k5Id)
  let tableKey = asset.tableKey
  if (asset.id === 'water-treatment') tableKey = input.variant === 'double' ? 'waterTreatmentDouble' : 'waterTreatmentSingle'
  if (asset.id === 'water-pump') tableKey = input.variant === 'high' ? 'waterPumpHighHead' : 'waterPumpLowHead'
  const table = tables[tableKey!] as TableSeries
  const hasBreakdown = table.points.some((point) => typeof point.total === 'number')
  const totalEval = evaluate(table.points, parameter, hasBreakdown ? 'total' : 'value', {
    highRegression: table.highExtrapolation === 'regression3', capAt: table.capAt,
  })
  let adjustmentFactor = decimal(1)
  const adjustments = [`Polohový koeficient K5: ${k5.value.toFixed(2).replace('.', ',')}`]
  if (asset.id === 'water-treatment') {
    if (input.k1 < 0.5 || input.k1 > 2) throw new Error('Koeficient K1 musí být v rozmezí 0,50 až 2,00.')
    adjustmentFactor = decimal(input.k1).plus(input.hardening ? 0.1 : 0).plus(input.ozonation ? 0.1 : 0)
    adjustments.push(`Koeficient úpravny: ${adjustmentFactor.toFixed(2).replace('.', ',')}`)
    if (input.hardening) adjustments.push('Ztvrzování vody: +10 %')
    if (input.ozonation) adjustments.push('Ozonizace: +10 %')
  }
  const atypicalCost = nonNegative(input.atypicalCost, 'Atypické náklady')
  const indicator = totalEval.value.times(adjustmentFactor)
  const indicatorGross = exactNumber(indicator)
  const baseTotal = indicator.times(1000).times(k5.value)
  const rawTotal = baseTotal.plus(atypicalCost)
  const totalGross = crown(rawTotal)
  const totalNet = moneyNet(rawTotal)
  const rawBreakdown: Array<{ label: string; rawGross: Decimal.Value }> = []
  if (hasBreakdown) {
    const building = evaluate(table.points, parameter, 'building', {
      highRegression: table.highExtrapolation === 'regression3', capAt: table.capAt,
    })
    const technology = evaluate(table.points, parameter, 'technology', {
      highRegression: table.highExtrapolation === 'regression3', capAt: table.capAt,
    })
    const buildingRaw = building.value.times(1000).times(adjustmentFactor).times(k5.value)
    const technologyRaw = technology.value.times(1000).times(adjustmentFactor).times(k5.value)
    rawBreakdown.push(
      { label: 'Stavební část', rawGross: buildingRaw },
      { label: 'Technologická část', rawGross: technologyRaw },
    )
  } else if (asset.id === 'water-pump' || asset.id === 'sewer-pump') {
    const halfRaw = baseTotal.div(2)
    rawBreakdown.push(
      { label: 'Stavební část (50 %)', rawGross: halfRaw },
      { label: 'Technologická část (50 %)', rawGross: baseTotal.minus(halfRaw) },
    )
  }
  if (atypicalCost) {
    rawBreakdown.push({ label: 'Atypické náklady', rawGross: decimal(atypicalCost) })
    adjustments.push(`Atypické náklady: ${formatMoney(atypicalCost)}`)
  }
  const warnings = totalEval.method === 'extrapolated'
    ? ['Parametr je mimo rozsah tabulky a výsledný cenový ukazatel je pracovně extrapolován.']
    : []
  if (asset.id === 'water-treatment' && !adjustmentFactor.eq(1)) warnings.push('Koeficient K1 a technologické příplatky musí potvrdit odborný zpracovatel.')
  return makeBaseResult(input, {
    inputSummary: `${asset.parameterLabel}: ${parameter.toLocaleString('cs-CZ')} ${asset.parameterUnit}${asset.variants ? `, ${asset.variants.find((variant) => variant.value === input.variant)?.label ?? input.variant}` : ''}`,
    indicatorLabel: 'Cenový ukazatel Cu',
    indicatorGross,
    indicatorNet: indicatorNet(indicator),
    indicatorUnit: 'tis. Kč/objekt',
    totalGross,
    totalNet,
    method: totalEval.method,
    formula: `${totalEval.formula} Cu po úpravách: ${formulaNumber(indicator)} tis. Kč; celkem: ${formulaNumber(indicator)} × 1 000 × K5 ${k5.value}; konečná cena ${totalGross} Kč.`,
    sourceLabel: `Metodický pokyn ${methodology.meta.methodology}, tabulka č. ${asset.table}`,
    sourcePoints: totalEval.sourcePoints,
    breakdown: makeBreakdown(rawBreakdown, totalGross, totalNet),
    warnings,
    adjustments,
  })
}

function calculateWwtp(input: CalculationInput): CalculationResult {
  const eo = positive(input.parameter, 'Počet EO')
  const k5 = findK5(input.k5Id)
  const tableKey = eo >= 5000 ? 'wwtpLarge' : 'wwtpSmall'
  const table = tables[tableKey] as TableSeries
  const options = { capAt: table.capAt }
  const totalEval = evaluate(table.points, eo, 'total', options)
  const technology = evaluate(table.points, eo, 'technology', options)
  const building = evaluate(table.points, eo, 'building', options)
  const atypicalCost = nonNegative(input.atypicalCost, 'Atypické náklady')
  const baseTotal = totalEval.value.times(eo).times(k5.value)
  const rawTotal = baseTotal.plus(atypicalCost)
  const totalGross = crown(rawTotal)
  const totalNet = moneyNet(rawTotal)
  const technologyRaw = technology.value.times(eo).times(k5.value)
  const buildingRaw = building.value.times(eo).times(k5.value)
  const warnings = totalEval.method === 'extrapolated'
    ? ['Počet EO je mimo rozsah příslušné tabulky a hodnota je pracovně extrapolována.']
    : []
  return makeBaseResult(input, {
    inputSummary: `${eo.toLocaleString('cs-CZ')} EO, ${eo >= 5000 ? 'tabulka 7b' : 'tabulka 7a'}`,
    indicatorLabel: 'Měrný cenový ukazatel Cmu',
    indicatorGross: exactNumber(totalEval.value),
    indicatorNet: indicatorNet(totalEval.value),
    indicatorUnit: 'Kč/EO',
    totalGross,
    totalNet,
    method: totalEval.method,
    formula: `${totalEval.formula} Celkem: ${formulaNumber(totalEval.value)} × ${eo} × ${k5.value}; konečná cena ${totalGross} Kč.`,
    sourceLabel: `Metodický pokyn ${methodology.meta.methodology}, tabulka č. ${eo >= 5000 ? '7b' : '7a'}`,
    sourcePoints: totalEval.sourcePoints,
    breakdown: makeBreakdown([
      { label: 'Stavební část', rawGross: buildingRaw },
      { label: 'Technologická část', rawGross: technologyRaw },
      ...(atypicalCost ? [{ label: 'Atypické náklady', rawGross: decimal(atypicalCost) }] : []),
    ], totalGross, totalNet),
    warnings,
    adjustments: [`Polohový koeficient K5: ${k5.value.toFixed(2).replace('.', ',')}`],
  })
}

function calculateFixed(input: CalculationInput): CalculationResult {
  const asset = assetById.get(input.assetId)!
  const fixed = methodology.fixedAssets.find((item) => item.id === asset.fixedId)
  if (!fixed) throw new Error('Pevná sazba nebyla nalezena.')
  const k5 = findK5(input.k5Id)
  const parameter = fixed.kind === 'rate' ? positive(input.parameter, fixed.parameter ?? 'Technický parametr') : 1
  const atypicalCost = nonNegative(input.atypicalCost, 'Atypické náklady')
  const baseTotal = decimal(fixed.value).times(parameter).times(k5.value)
  const rawTotal = baseTotal.plus(atypicalCost)
  const totalGross = crown(rawTotal)
  const totalNet = moneyNet(rawTotal)
  return makeBaseResult(input, {
    inputSummary: fixed.kind === 'rate' ? `${fixed.parameter}: ${parameter.toLocaleString('cs-CZ')} ${fixed.parameterUnit}` : '1 objekt',
    indicatorLabel: fixed.kind === 'rate' ? 'Měrný cenový ukazatel Cmu' : 'Cenový ukazatel Cu',
    indicatorGross: fixed.value,
    indicatorNet: indicatorNet(fixed.value),
    indicatorUnit: fixed.unit,
    totalGross,
    totalNet,
    method: 'fixed',
    formula: fixed.kind === 'rate' ? `${fixed.value} × ${parameter} × K5 ${k5.value}.` : `${fixed.value} × K5 ${k5.value}.`,
    sourceLabel: `Metodický pokyn ${methodology.meta.methodology}, ${fixed.source}`,
    sourcePoints: [],
    breakdown: atypicalCost ? makeBreakdown([
      { label: 'Základní cena', rawGross: baseTotal },
      { label: 'Atypické náklady', rawGross: decimal(atypicalCost) },
    ], totalGross, totalNet) : [],
    warnings: fixed.id === 'holding-tank' ? ['Metodika stanoví tuto cenu výhradně pro bezodtokou jímku o objemu 5 m³.'] : [],
    adjustments: [`Polohový koeficient K5: ${k5.value.toFixed(2).replace('.', ',')}`],
  })
}

function calculateSpring(input: CalculationInput): CalculationResult {
  const volume = positive(input.parameter, 'Užitný objem jímky')
  const length = positive(input.length, 'Délka potrubí')
  const dn = positive(input.dn, 'DN potrubí')
  const material = input.material === '__other__' ? input.baseMaterial : input.material
  const materialCoefficient = input.material === '__other__' ? input.materialCoefficient : 1
  if (materialCoefficient < 0.8 || materialCoefficient > 1.8) throw new Error('Koeficient materiálu musí být v intervalu 0,8 až 1,8.')
  const k5 = findK5(input.k5Id)
  const reservoir = tables.groundReservoir as TableSeries
  const reservoirEval = evaluate(reservoir.points, volume, 'value', { highRegression: true })
  const pipeEval = getPipeEvaluation('water-pipe', material, input.surface, dn)
  const reservoirRaw = reservoirEval.value.times(1000).times(k5.value)
  const pipeRaw = pipeEval.value.times(materialCoefficient).times(length).times(k5.value)
  const rawTotal = reservoirRaw.plus(pipeRaw)
  const totalGross = crown(rawTotal)
  const totalNet = moneyNet(rawTotal)
  const method = combineMethod([reservoirEval.method, pipeEval.method])
  return makeBaseResult(input, {
    inputSummary: `Objem ${volume.toLocaleString('cs-CZ')} m³; potrubí DN ${dn}, ${material}, ${length.toLocaleString('cs-CZ')} bm`,
    indicatorLabel: 'Součet dílčích cen před K5',
    indicatorGross: exactNumber(reservoirEval.value.times(1000).plus(pipeEval.value.times(materialCoefficient).times(length))),
    indicatorNet: indicatorNet(reservoirEval.value.times(1000).plus(pipeEval.value.times(materialCoefficient).times(length))),
    indicatorUnit: 'Kč',
    totalGross,
    totalNet,
    method,
    formula: `Zemní vodojem: ${formulaNumber(reservoirEval.value)} tis. Kč; potrubí: ${formulaNumber(pipeEval.value)} × ${length}; součet × K5 ${k5.value}; konečná cena ${totalGross} Kč.`,
    sourceLabel: `Metodický pokyn ${methodology.meta.methodology}, čl. IV odst. 2, tabulky č. 2 a 6`,
    sourcePoints: [...reservoirEval.sourcePoints, ...pipeEval.sourcePoints],
    breakdown: makeBreakdown([
      { label: 'Jímka jako zemní vodojem', rawGross: reservoirRaw },
      { label: 'Navazující potrubí', rawGross: pipeRaw },
    ], totalGross, totalNet),
    warnings: method === 'extrapolated' ? ['Nejméně jedna část složeného výpočtu je pracovně extrapolována.'] : [],
    adjustments: [`Polohový koeficient K5: ${k5.value.toFixed(2).replace('.', ',')}`],
  })
}

function calculateSimplified(input: CalculationInput): CalculationResult {
  const table = tables.simplified as SimplifiedTable
  const row = table.rows[input.simplifiedIndex]
  if (!row) throw new Error('Vyberte platný druh majetku pro přepočet.')
  const oldPrice = positive(input.oldPrice, 'Původní cena')
  const rawTotal = decimal(oldPrice).times(row.coefficient)
  const totalGross = crown(rawTotal)
  return makeBaseResult(input, {
    inputSummary: `${row.label}; původní cena ${oldPrice.toLocaleString('cs-CZ')} Kč`,
    indicatorLabel: 'Přepočtový koeficient',
    indicatorGross: row.coefficient,
    indicatorNet: row.coefficient,
    indicatorUnit: 'koeficient',
    totalGross,
    totalNet: moneyNet(rawTotal),
    method: 'converted',
    formula: `${oldPrice} × ${row.coefficient.toFixed(2).replace('.', ',')} = ${totalGross}.`,
    sourceLabel: `Metodický pokyn ${methodology.meta.methodology}, tabulka č. 13`,
    sourcePoints: [],
    breakdown: [],
    warnings: ['Zjednodušený přepočet lze použít pouze při splnění podmínek uvedených v článku VII metodického pokynu.'],
    adjustments: [],
  })
}

export function calculate(input: CalculationInput): CalculationResult {
  const asset = assetById.get(input.assetId)
  if (!asset) throw new Error('Vyberte platný druh majetku.')
  switch (asset.kind) {
    case 'pipe': return calculatePipe(input)
    case 'noncircular': return calculateNoncircular(input)
    case 'table-cu': return calculateTableCu(input)
    case 'wwtp': return calculateWwtp(input)
    case 'fixed': return calculateFixed(input)
    case 'composite-spring': return calculateSpring(input)
    case 'simplified': return calculateSimplified(input)
  }
}

export function formatMoney(value: Decimal.Value): string {
  return `${crown(value).toLocaleString('cs-CZ')} Kč`
}

export function formatIndicator(value: number, unit: string): string {
  const isThousands = unit.startsWith('tis. Kč')
  const rounded = decimal(value).toDecimalPlaces(3, Decimal.ROUND_HALF_UP).toNumber()
  const formatted = rounded.toLocaleString('cs-CZ', {
    minimumFractionDigits: isThousands ? 3 : 0,
    maximumFractionDigits: 3,
  })
  return `${formatted} ${unit}`
}
